import { useEffect, useRef, useState } from "react";
import { SettingsRequestError, settingsApi, settingsSaveErrorMessage } from "./api";
import { manualImageSubmissionErrorMessage } from "./manual-image";
import type { ImagePromptPreset, ManualImagePublicJob } from "./types";
import "./image-presets.css";

export interface ImagePromptTransfer {
  positive_prompt: string;
  negative_prompt: string;
}

interface ImagePresetsSettingsProps {
  active: boolean;
  /** 外部保存预设后递增，用于回到本页前同步列表。 */
  refreshKey?: number | string;
  onFill: (prompts: ImagePromptTransfer) => void;
  onJobQueued?: (job: ManualImagePublicJob) => void;
  onUsageChanged?: () => void;
}

function promptExcerpt(value: string): string {
  const normalized = value.trim().replace(/\s+/g, " ");
  return normalized.length > 160 ? `${normalized.slice(0, 160)}…` : normalized;
}

interface PendingMutation {
  fingerprint: string;
  requestId: string;
}

function stableMutationRequest(ref: { current: Map<string, PendingMutation> }, key: string, fingerprint: string): string {
  const previous = ref.current.get(key);
  if (previous?.fingerprint === fingerprint) return previous.requestId;
  const requestId = crypto.randomUUID();
  ref.current.set(key, { fingerprint, requestId });
  return requestId;
}

/** 预设是个人私有的题词快照；名称可重复，只有显示名称可以编辑。 */
export function ImagePresetsSettings({ active, refreshKey = 0, onFill, onJobQueued, onUsageChanged }: ImagePresetsSettingsProps) {
  const [presets, setPresets] = useState<ImagePromptPreset[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [workingId, setWorkingId] = useState<string | null>(null);
  const mountedRef = useRef(false);
  const generateRequestsRef = useRef(new Map<string, PendingMutation>());
  const renameRequestsRef = useRef(new Map<string, PendingMutation>());
  const deleteRequestsRef = useRef(new Map<string, PendingMutation>());

  async function refresh(): Promise<void> {
    if (!mountedRef.current) return;
    setLoading(true);
    setError("");
    try {
      const page = await settingsApi.loadImagePromptPresets();
      if (mountedRef.current) {
        setPresets(page.presets);
        setNextCursor(page.next_cursor);
      }
    } catch {
      if (mountedRef.current) setError("无法读取图片预设，请稍后重试。");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    if (!active) {
      mountedRef.current = false;
      return;
    }
    mountedRef.current = true;
    void refresh();
    return () => { mountedRef.current = false; };
  // active 是唯一的可见性门；外部保存会改变 refreshKey，显示期间才重新同步。
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh 读取最新 mounted ref。
  }, [active, refreshKey]);

  async function loadMore(): Promise<void> {
    if (!nextCursor || loadingMore) return;
    const cursor = nextCursor;
    setLoadingMore(true);
    setError("");
    try {
      const page = await settingsApi.loadImagePromptPresets(cursor);
      if (!mountedRef.current) return;
      setPresets((current) => {
        const existing = new Set(current.map((preset) => preset.id));
        return [...current, ...page.presets.filter((preset) => !existing.has(preset.id))];
      });
      setNextCursor(page.next_cursor);
    } catch {
      if (mountedRef.current) setError("无法加载更多图片预设，请稍后重试。");
    } finally {
      if (mountedRef.current) setLoadingMore(false);
    }
  }

  async function saveTitle(preset: ImagePromptPreset): Promise<void> {
    const title = titleDraft.trim();
    if (!title) { setError("预设名称不能为空。"); return; }
    if (title.length > 100) { setError("预设名称不能超过 100 个字符。"); return; }
    setWorkingId(preset.id);
    setError("");
    setNotice("");
    const fingerprint = `${preset.id}\u0000${preset.revision}\u0000${title}`;
    const requestId = stableMutationRequest(renameRequestsRef, preset.id, fingerprint);
    try {
      const updated = await settingsApi.renameImagePromptPreset(preset.id, requestId, title, preset.revision);
      if (!mountedRef.current) return;
      renameRequestsRef.current.delete(preset.id);
      setPresets((current) => current.map((item) => item.id === updated.id ? updated : item));
      setEditingId(null);
      setNotice("已保存预设名称。");
    } catch (reason) {
      if (!mountedRef.current) return;
      if (reason instanceof SettingsRequestError && reason.status === 409) {
        renameRequestsRef.current.delete(preset.id);
        await refresh();
      }
      if (mountedRef.current) setError(settingsSaveErrorMessage(reason));
    } finally {
      if (mountedRef.current) setWorkingId(null);
    }
  }

  async function generate(preset: ImagePromptPreset): Promise<void> {
    if (workingId) return;
    setWorkingId(preset.id);
    setError("");
    setNotice("");
    const fingerprint = `${preset.id}\u0000${preset.positive_prompt}\u0000${preset.negative_prompt}`;
    const requestId = stableMutationRequest(generateRequestsRef, preset.id, fingerprint);
    try {
      const job = await settingsApi.createManualImageJob(requestId, preset.positive_prompt, preset.negative_prompt);
      if (!mountedRef.current) return;
      generateRequestsRef.current.delete(preset.id);
      onJobQueued?.(job);
      onUsageChanged?.();
      setNotice("已加入队列。请前往“任务队列”查看生成进度。");
    } catch (reason) {
      if (mountedRef.current) setError(manualImageSubmissionErrorMessage(reason));
    } finally {
      if (mountedRef.current) setWorkingId(null);
    }
  }

  async function remove(preset: ImagePromptPreset): Promise<void> {
    if (workingId) return;
    setWorkingId(preset.id);
    setError("");
    setNotice("");
    const fingerprint = `${preset.id}\u0000${preset.revision}`;
    const requestId = stableMutationRequest(deleteRequestsRef, preset.id, fingerprint);
    try {
      await settingsApi.deleteImagePromptPreset(preset.id, requestId, preset.revision);
      if (mountedRef.current) {
        deleteRequestsRef.current.delete(preset.id);
        setPresets((current) => current.filter((item) => item.id !== preset.id));
        setNotice("已删除预设。");
      }
    } catch (reason) {
      if (!mountedRef.current) return;
      if (reason instanceof SettingsRequestError && reason.status === 409) {
        deleteRequestsRef.current.delete(preset.id);
        await refresh();
      }
      if (mountedRef.current) setError(settingsSaveErrorMessage(reason));
    } finally {
      if (mountedRef.current) setWorkingId(null);
    }
  }

  if (!active) return null;
  return <section className="image-presets" aria-label="图片预设列表">
    <header className="image-presets-heading"><div><h3>预设列表</h3><p>保存个人常用的正面、负面提示词。</p></div><button className="secondary outlined" type="button" disabled={loading} onClick={() => void refresh()}>刷新</button></header>
    {error ? <p className="action-error" role="alert">{error}</p> : null}
    {notice ? <p className="image-preset-notice" role="status">{notice}</p> : null}
    {loading ? <p className="panel-state">正在读取图片预设…</p> : presets.length === 0 ? <p className="panel-state">还没有图片预设。可在“新建任务”中保存一组题词。</p> : <div className="image-presets-list">
      {presets.map((preset) => <article className="image-preset-row" key={preset.id}>
        <div className="image-preset-copy">
          {editingId === preset.id ? <form className="image-preset-title-editor" onSubmit={(event) => { event.preventDefault(); void saveTitle(preset); }}><input aria-label="预设名称" autoFocus maxLength={100} value={titleDraft} disabled={workingId === preset.id} onChange={(event) => setTitleDraft(event.target.value)} /><button className="secondary outlined" type="submit" disabled={workingId === preset.id}>保存</button><button className="secondary text" type="button" disabled={workingId === preset.id} onClick={() => setEditingId(null)}>取消</button></form> : <button className="image-preset-title" type="button" onClick={() => { setEditingId(preset.id); setTitleDraft(preset.title); setError(""); }}>{preset.title}</button>}
          <p><b>正面：</b>{promptExcerpt(preset.positive_prompt)}</p>
          <p><b>负面：</b>{promptExcerpt(preset.negative_prompt) || "（空）"}</p>
        </div>
        <div className="image-preset-actions">
          <button className="secondary outlined" type="button" disabled={workingId !== null} onClick={() => { onFill({ positive_prompt: preset.positive_prompt, negative_prompt: preset.negative_prompt }); setError(""); setNotice("已填充到“新建任务”。"); }}>填充</button>
          <button className="primary" type="button" disabled={workingId !== null} onClick={() => void generate(preset)}>{workingId === preset.id ? "处理中…" : "生成"}</button>
          <button className="danger outlined" type="button" disabled={workingId !== null} onClick={() => void remove(preset)}>删除</button>
        </div>
      </article>)}
    </div>}
    {!loading && presets.length > 0 && nextCursor ? <div className="image-presets-more"><button className="secondary outlined" type="button" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? "加载中…" : "加载更多"}</button></div> : null}
  </section>;
}
